import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, Link, Navigate, useLocation } from 'react-router-dom';
import { 
  MessageSquare, 
  Send, 
  Play,
  Pause,
  Square,
  Target,
  Brain,
  CheckCircle,
  XCircle,
  Lightbulb,
  Loader2,
  Archive,
  Clock,
  Volume2,
  VolumeX
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useVoiceoverStore } from '@/store/voiceoverStore';
import io, { Socket } from 'socket.io-client';
import PageHeader from '@/components/PageHeader';
import { logger } from '@/utils/logger';
import { getSocketUrl } from '@/utils/socketUrl';
import { getVoiceoverAvatar } from '@/config/voiceoverAvatars';
import StudentChatMessage from '@/components/StudentChatMessage';
import { createTypingNotifier } from '@/utils/typingNotifier';

interface Message {
  id: number;
  room_id: number;
  user_id: number | null;
  message_text?: string; // Legacy field
  content?: string; // New field from backend
  message_type: 'text' | 'chat' | 'hint' | 'answer' | 'system' | 'llm' | 'question';
  created_at: string;
  // User info directly on message object (from backend)
  username?: string;
  user_name?: string;
  user_role?: string;
  // Legacy user object
  user?: {
    username: string;
    name: string;
    role: 'curator' | 'student';
  };
  metadata?: {
    score?: number;
    feedback?: string;
    hint?: string;
    questionId?: number;
    stageId?: number;
    action?: string;
    studentId?: number;
    isCorrect?: boolean;
    isWelcome?: boolean;
    actor?: string;
    characterName?: string;
  };
}

interface Room {
  id: number;
  name: string;
  subject_id: number;
  created_by: number;
  status: 'active' | 'paused' | 'completed' | 'archived';
  current_stage_id?: number;
  current_stage_name?: string;
  current_question_id?: number;
  current_question_text?: string;
  min_success_score: number;
  hint_threshold: number;
  allowed_consequent_answers: number;
  current_question_attempts?: number;
  /** Personal (individual) room: assigned student */
  is_personal?: number | boolean;
  personal_student_id?: number | null;
  subject?: {
    name: string;
  };
  creator?: {
    username: string;
    name: string;
  };
}

interface Session {
  active: boolean;
  session?: {
    roomId: number;
    currentQuestion: string;
    questionId: number;
    questionNumber?: number; // Total question number in session (1-based)
    stageId: number;
    status: 'active' | 'paused' | 'completed';
    isAnswerSubmitted: boolean;
    answerCount: number;
    minSuccessScore: number;
  };
}

interface TypingUser {
  userId: number;
  name: string;
  timestamp: number;
}

export const ChatPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const location = useLocation();
  const { user, isAuthenticated, isCurator } = useAuthStore();
  const { isVoiceoverEnabled, voiceoverVolume, selectedAvatarId, selectedVoiceURI, selectedVoiceLang } = useVoiceoverStore();
  const [room, setRoom] = useState<Room | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<Set<number>>(new Set());
  const [isAnswerInputLocked, setIsAnswerInputLocked] = useState(false);
  const isStudentInputLocked = isAnswerInputLocked && user?.role === 'student';
  const [session, setSession] = useState<Session | null>(null);
  const [isLlmThinking, setIsLlmThinking] = useState(false);
  const [llmScope, setLlmScope] = useState<string | null>(null);
  const [thinkingCharacterName, setThinkingCharacterName] = useState('Alex');
  const [isStartingSession, setIsStartingSession] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [timeLimitSeconds, setTimeLimitSeconds] = useState<number | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingNotifier = useMemo(() => createTypingNotifier(socket, Number(roomId)), [socket, roomId]);
  const hasAutoPlayedVoiceoverRef = useRef(false);
  const syntheticMessageIdRef = useRef(-1);
  const activeVoiceoverMessageRef = useRef<Message | null>(null);
  const voiceoverProgressTimerRef = useRef<number | null>(null);
  const [speakingMessageId, setSpeakingMessageId] = useState<number | null>(null);
  const [voiceoverProgress, setVoiceoverProgress] = useState(0);
  const isSpeechSupported = typeof window !== 'undefined' && 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
  const isIOS = typeof navigator !== 'undefined'
    && (
      /iPad|iPhone|iPod/.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );

  const isVoiceoverMessage = useCallback((message: Message): boolean => {
    return message.message_type === 'llm' || message.message_type === 'question';
  }, []);

  const cleanVoiceoverText = useCallback((text: string): string => {
    return text
      .replace(/[\uFE0E\uFE0F]/gu, '')
      .replace(/\p{Extended_Pictographic}/gu, '')
      .replace(/\bscore\s*:\s*\d{1,3}\s*%?/gi, '')
      .replace(/\b\d{1,3}\s*%\s*score\b/gi, '')
      .replace(/^\s*\d{1,3}\s*%?\s*(feedback\s*:?)?/i, '')
      .replace(/^feedback\s*:\s*/i, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }, []);

  const getMessageVoiceText = useCallback((message: Message): string => {
    if (message.message_type === 'question') {
      const questionText = message.content || message.message_text || '';
      return questionText ? `Question. ${questionText}` : '';
    }

    if (message.message_type !== 'llm') {
      return '';
    }

    const isEvaluation = message.metadata?.score !== undefined || Boolean(message.metadata?.feedback);
    const parts = isEvaluation
      ? [
          message.metadata?.feedback || '',
          message.metadata?.hint ? `Hint: ${message.metadata.hint}` : ''
        ]
      : [
          message.content || message.message_text || '',
          message.metadata?.hint ? `Hint: ${message.metadata.hint}` : ''
        ];
    const seenParts = new Set<string>();

    return parts
      .map(part => part.trim())
      .map(cleanVoiceoverText)
      .filter(Boolean)
      .filter((part) => {
        const key = part.toLowerCase();
        if (seenParts.has(key)) {
          return false;
        }
        seenParts.add(key);
        return true;
      })
      .join('. ');
  }, [cleanVoiceoverText]);

  const getPreferredVoice = useCallback((): SpeechSynthesisVoice | null => {
    if (!isSpeechSupported) {
      return null;
    }

    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      return null;
    }

    const selectedVoice = voices.find(voice => voice.voiceURI === selectedVoiceURI);
    if (selectedVoice) {
      return selectedVoice;
    }

    const preferredLang = selectedVoiceLang || navigator.language || 'en';
    const exactLangVoice = voices.find(voice => voice.lang === preferredLang);
    if (exactLangVoice) {
      return exactLangVoice;
    }

    const baseLang = preferredLang.split('-')[0] || preferredLang;
    return voices.find(voice => voice.lang.toLowerCase().startsWith(baseLang.toLowerCase()))
      || voices.find(voice => voice.default)
      || voices[0]
      || null;
  }, [isSpeechSupported, selectedVoiceLang, selectedVoiceURI]);

  const estimateVoiceoverDuration = useCallback((text: string): number => {
    const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
    const wordsPerMinute = 155;
    return Math.max(1200, (wordCount / wordsPerMinute) * 60_000);
  }, []);

  const clearVoiceoverProgressTimer = useCallback(() => {
    if (voiceoverProgressTimerRef.current !== null) {
      window.clearInterval(voiceoverProgressTimerRef.current);
      voiceoverProgressTimerRef.current = null;
    }
  }, []);

  const startVoiceoverProgress = useCallback((text: string) => {
    clearVoiceoverProgressTimer();
    setVoiceoverProgress(0);

    const startedAt = Date.now();
    const estimatedDuration = estimateVoiceoverDuration(text);

    voiceoverProgressTimerRef.current = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setVoiceoverProgress((currentProgress) => Math.max(
        currentProgress,
        Math.min(0.96, elapsed / estimatedDuration)
      ));
    }, 100);
  }, [clearVoiceoverProgressTimer, estimateVoiceoverDuration]);

  const stopVoiceover = useCallback(() => {
    if (!isSpeechSupported) {
      return;
    }

    activeVoiceoverMessageRef.current = null;
    window.speechSynthesis.cancel();
    setSpeakingMessageId(null);
    clearVoiceoverProgressTimer();
    setVoiceoverProgress(0);
  }, [clearVoiceoverProgressTimer, isSpeechSupported]);

  const finishVoiceoverProgress = useCallback(() => {
    clearVoiceoverProgressTimer();
    setVoiceoverProgress(0);
  }, [clearVoiceoverProgressTimer]);

  const speakMessage = useCallback((message: Message, volume = voiceoverVolume, interruptCurrent = true) => {
    if (!isSpeechSupported) {
      return;
    }

    const text = getMessageVoiceText(message);
    if (!text) {
      return;
    }

    if (interruptCurrent && activeVoiceoverMessageRef.current) {
      activeVoiceoverMessageRef.current = null;
      window.speechSynthesis.cancel();
      finishVoiceoverProgress();
    }

    window.speechSynthesis.resume();

    const utterance = new SpeechSynthesisUtterance(text);
    const selectedVoice = getPreferredVoice();
    if (selectedVoice) {
      utterance.voice = selectedVoice;
      utterance.lang = selectedVoice.lang;
    }
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = volume;
    const handleSpeechComplete = () => {
      if (activeVoiceoverMessageRef.current?.id === message.id) {
        activeVoiceoverMessageRef.current = null;
        setSpeakingMessageId(null);
        finishVoiceoverProgress();
      }
    };
    utterance.onboundary = (event) => {
      if (event.charIndex > 0 && text.length > 0) {
        setVoiceoverProgress((currentProgress) => Math.max(
          currentProgress,
          Math.min(0.98, event.charIndex / text.length)
        ));
      }
    };
    utterance.onend = handleSpeechComplete;
    utterance.onerror = handleSpeechComplete;

    activeVoiceoverMessageRef.current = message;
    setSpeakingMessageId(message.id);
    startVoiceoverProgress(text);
    window.speechSynthesis.speak(utterance);
  }, [finishVoiceoverProgress, getMessageVoiceText, getPreferredVoice, isSpeechSupported, startVoiceoverProgress, voiceoverVolume]);

  const storeMessage = useCallback((message: Message): boolean => {
    if (!message.id || !message.created_at) {
      logger.warn('Invalid message received:', { id: message.id, created_at: message.created_at });
      return false;
    }

    setMessages((currentMessages) => {
      const existingIdIndex = currentMessages.findIndex(existingMessage => existingMessage.id === message.id);
      if (existingIdIndex !== -1) {
        const nextMessages = [...currentMessages];
        nextMessages[existingIdIndex] = message;
        return nextMessages;
      }

      const questionId = message.metadata?.questionId;
      if (message.message_type === 'question' && questionId) {
        const existingQuestionIndex = currentMessages.findIndex((existingMessage) => (
          existingMessage.message_type === 'question'
          && existingMessage.metadata?.questionId === questionId
        ));

        if (existingQuestionIndex !== -1) {
          const existingQuestion = currentMessages[existingQuestionIndex];
          if (!existingQuestion) {
            return currentMessages;
          }

          if (existingQuestion.id < 0 && message.id > 0) {
            const nextMessages = [...currentMessages];
            nextMessages[existingQuestionIndex] = message;
            return nextMessages;
          }

          return currentMessages;
        }
      }

      return [...currentMessages, message];
    });

    return true;
  }, []);

  const showQuestionFromSessionEvent = useCallback((data: {
    roomId?: number;
    question?: string;
    questionId?: number;
    stageId?: number;
  }) => {
    const currentRoomId = Number(roomId);
    if (!currentRoomId || data?.roomId !== currentRoomId || !data.question || !data.questionId) {
      return;
    }

    const metadata: Message['metadata'] = {
      questionId: data.questionId,
      action: 'question_asked'
    };
    if (data.stageId !== undefined) {
      metadata.stageId = data.stageId;
    }

    storeMessage({
      id: syntheticMessageIdRef.current--,
      room_id: currentRoomId,
      user_id: 0,
      content: data.question,
      message_type: 'question',
      created_at: new Date().toISOString(),
      metadata
    });
  }, [roomId, storeMessage]);

  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (!isVoiceoverEnabled || !isSpeechSupported || isIOS) {
      return;
    }

    if (hasAutoPlayedVoiceoverRef.current || activeVoiceoverMessageRef.current) {
      return;
    }

    const voiceoverMessages = messages.filter((message) => (
      Boolean(message.id)
      && isVoiceoverMessage(message)
      && Boolean(getMessageVoiceText(message))
    ));

    const introMessage = voiceoverMessages[0];
    if (voiceoverMessages.length === 1 && introMessage) {
      hasAutoPlayedVoiceoverRef.current = true;
      speakMessage(introMessage, voiceoverVolume, false);
    }
  }, [getMessageVoiceText, isIOS, isSpeechSupported, isVoiceoverEnabled, isVoiceoverMessage, messages, speakMessage, voiceoverVolume]);

  useEffect(() => {
    return () => {
      stopVoiceover();
    };
  }, [stopVoiceover]);

  useEffect(() => {
    if (!isSpeechSupported) {
      return;
    }

    const cancelSpeech = () => {
      activeVoiceoverMessageRef.current = null;
      window.speechSynthesis.cancel();
      setSpeakingMessageId(null);
      clearVoiceoverProgressTimer();
      setVoiceoverProgress(0);
    };

    window.addEventListener('pagehide', cancelSpeech);
    window.addEventListener('beforeunload', cancelSpeech);

    return () => {
      window.removeEventListener('pagehide', cancelSpeech);
      window.removeEventListener('beforeunload', cancelSpeech);
    };
  }, [clearVoiceoverProgressTimer, isSpeechSupported]);

  const fetchSessionStatus = useCallback(async () => {
    if (!roomId) return;

    try {
      const response = await fetch(`/api/sessions/${roomId}/status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        const sessionData = await response.json();
        setSession(sessionData);

        setIsAnswerInputLocked(Boolean(sessionData.active && sessionData.session?.isAnswerSubmitted));
      }
    } catch (error) {
      console.error('Failed to fetch session status:', error);
    }
  }, [roomId]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!isAuthenticated || !roomId) return;

    const currentRoomId = Number(roomId);
    if (!currentRoomId) return;

    const newSocket = io(getSocketUrl(), {
      auth: {
        token: localStorage.getItem('teaching_app_token')
      }
    });

    newSocket.on('connect', () => {
      logger.debug('Connected to WebSocket server');
    });

    newSocket.on('authenticated', () => {
      logger.debug('WebSocket authenticated');
      logger.debug('Emitting join_room for roomId:', roomId);
      newSocket.emit('join_room', { roomId: currentRoomId });
    });

    newSocket.on('connect_error', (error) => {
      logger.error('WebSocket connection error:', error);
    });

    newSocket.on('disconnect', (reason) => {
      logger.debug('WebSocket disconnected:', reason);
      setTypingUsers([]);
    });

    newSocket.on('room_joined', (data) => {
      logger.debug('Joined room:', data);
    });

    newSocket.on('new_message', (message: Message) => {
      logger.socket('Received new_message via WebSocket:', message);
      if (message.user_id != null) setTypingUsers(prev => prev.filter(person => person.userId !== message.user_id));
      if (!storeMessage(message)) {
        return;
      }
      
      const unlocksAnswerInput = message.message_type === 'question'
        || (message.message_type === 'system' && message.metadata?.action === 'session_start');

      if (unlocksAnswerInput) {
        setIsAnswerInputLocked(false);
      }
    });

    newSocket.on('user_joined', (data) => {
      setOnlineUsers(prev => new Set([...prev, data.userId]));

      // Do not show "X joined" in chat when a curator enters (students should not see that)
      if (data.role === 'curator') {
        return;
      }
      const systemMessage: Message = {
        id: Date.now(),
        room_id: currentRoomId,
        user_id: 0,
        content: `${data.username} joined the room`,
        message_type: 'system',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, systemMessage]);
    });

    newSocket.on('user_left', (data) => {
      setTypingUsers(prev => prev.filter(person => person.userId !== data.userId));
      setOnlineUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(data.userId);
        return newSet;
      });

      if (data.role === 'curator') {
        return;
      }
      const systemMessage: Message = {
        id: Date.now(),
        room_id: currentRoomId,
        user_id: 0,
        content: `${data.username} left the room`,
        message_type: 'system',
        created_at: new Date().toISOString()
      };
      setMessages(prev => [...prev, systemMessage]);
    });

    newSocket.on('typing_start', (data) => {
      logger.debug('Received typing_start:', data);
      if (data.roomId === currentRoomId && data.userId !== user?.id) {
        setTypingUsers(prev => {
          const filtered = prev.filter(u => u.userId !== data.userId);
          const name = typeof data.name === 'string' && data.name.trim() ? data.name.trim() : 'Student';
          const updated = [...filtered, { userId: data.userId, name, timestamp: Date.now() }];
          logger.debug('Updated typing users:', updated);
          return updated;
        });
      }
    });

    newSocket.on('typing_stop', (data) => {
      if (data.roomId !== currentRoomId) return;
      logger.debug('Received typing_stop:', data);
      setTypingUsers(prev => {
        const filtered = prev.filter(u => u.userId !== data.userId);
        logger.debug('Removed typing user, remaining:', filtered);
        return filtered;
      });
    });

    newSocket.on('error', (error) => {
      console.error('WebSocket error:', error);
    });

    newSocket.on('session_started', (data) => {
      logger.debug('Session started:', data);
      showQuestionFromSessionEvent(data);
      if (data.timeLimitSeconds) {
        setTimeLimitSeconds(data.timeLimitSeconds);
      }
      fetchSessionStatus();
    });

    newSocket.on('answer_submitted', (data) => {
      logger.debug('Answer submitted:', data);
      setIsAnswerInputLocked(true);
    });

    newSocket.on('answer_evaluated', (data) => {
      logger.debug('Answer evaluated:', data);
      fetchSessionStatus();
    });

    newSocket.on('question_changed', (data) => {
      logger.debug('Question changed:', data);
      showQuestionFromSessionEvent(data);
      setIsAnswerInputLocked(false);
      if (data.timeLimitSeconds) {
        setTimeLimitSeconds(data.timeLimitSeconds);
      }
      fetchSessionStatus();
    });
    
    newSocket.on('timer_state', (data: { roomId: number; remaining: number; isPaused: boolean; timeLimitSeconds: number }) => {
      if (data?.roomId !== currentRoomId) return;
      logger.debug('Timer state received:', data);
      setTimeLimitSeconds(data.timeLimitSeconds);
      setTimeRemaining(data.remaining);
    });
    
    newSocket.on('timer_update', (data: { roomId: number; remaining: number; isPaused: boolean }) => {
      if (data?.roomId !== currentRoomId) return;
      setTimeRemaining(data.remaining);
    });
    
    newSocket.on('question_timeout', (data) => {
      logger.debug('Question timeout:', data);
      setTimeRemaining(0);
      fetchSessionStatus();
    });

    newSocket.on('correct_answer_revealed', (data) => {
      logger.debug('Correct answer revealed:', data);
    });

    newSocket.on('session_completed', (data) => {
      logger.debug('Session completed:', data);
      setIsAnswerInputLocked(false);
      fetchSessionStatus();
    });

    newSocket.on('room_updated', (data: { room: Room }) => {
      logger.debug('Room updated event received in ChatPage:', data.room);
      if (data.room && data.room.id === currentRoomId) {
        setRoom(prev => prev ? { ...prev, ...data.room } : data.room);
        
        setSession(prev => {
          if (!prev || !prev.session) return prev;
          
          const roomStatus = data.room.status;
          let sessionStatus: 'active' | 'paused' | 'completed' = 'active';
          
          if (roomStatus === 'paused') {
            sessionStatus = 'paused';
          } else if (roomStatus === 'completed') {
            sessionStatus = 'completed';
          } else if (roomStatus === 'active') {
            sessionStatus = 'active';
          }
          
          return {
            ...prev,
            session: {
              ...prev.session,
              status: sessionStatus
            }
          };
        });
        
        fetchSessionStatus();
      }
    });

    newSocket.on('llm_thinking_start', (data: { roomId: number; scope?: string; studentId?: number; characterName?: string }) => {
      if (data?.roomId !== currentRoomId) return;
      setIsLlmThinking(true);
      setLlmScope(data?.scope || null);
      if (data.characterName) setThinkingCharacterName(data.characterName);
      if (data?.scope === 'answer_eval' || data?.scope === 'peer_hint') {
        setIsAnswerInputLocked(true);
      }
    });

    newSocket.on('llm_thinking_stop', (data: { roomId: number; scope?: string; studentId?: number }) => {
      if (data?.roomId !== currentRoomId) return;
      setIsLlmThinking(false);
      setLlmScope(null);
      if (data?.scope === 'answer_eval' || data?.scope === 'peer_hint') {
        fetchSessionStatus();
      }
    });
    
    newSocket.on('timer_paused', (data: { roomId: number; remaining?: number }) => {
      if (data?.roomId !== currentRoomId) return;
      if (data.remaining !== undefined) {
        setTimeRemaining(data.remaining);
      }
    });
    
    newSocket.on('timer_resumed', (data: { roomId: number; remaining?: number }) => {
      if (data?.roomId !== currentRoomId) return;
      if (data.remaining !== undefined && data.remaining > 0) {
        setTimeRemaining(data.remaining);
      }
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [fetchSessionStatus, isAuthenticated, roomId, showQuestionFromSessionEvent, storeMessage, user]);

  // Fetch room details and messages
  useEffect(() => {
    if (!roomId) return;

    const fetchRoomData = async () => {
      try {
        // Fetch room details
        const roomResponse = await fetch(`/api/rooms/${roomId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
          }
        });

        if (roomResponse.ok) {
          const roomData = await roomResponse.json();
          setRoom(roomData.room);
          
          // Individual rooms: auto-start only when not already triggered by POST /join (background).
          // Otherwise join + this duplicate and two startSession runs produce duplicate welcome/stage/question.
          const r = roomData.room as Room;
          const skipDupAutoStart = Boolean(
            (location.state as { skipDuplicatePersonalAutoStart?: boolean } | null)?.skipDuplicatePersonalAutoStart
          );
          if (
            !skipDupAutoStart &&
            user?.role === 'student' &&
            r &&
            (r.is_personal === 1 || r.is_personal === true) &&
            r.personal_student_id === user?.id
          ) {
            try {
              await fetch(`/api/rooms/${roomId}/auto-start-personal`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${localStorage.getItem('teaching_app_token')}`
                }
              });
            } catch {
              /* non-fatal */
            }
          }
        }

        // Fetch messages
        const messagesResponse = await fetch(`/api/messages/room/${roomId}?limit=50`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
          }
        });

        if (messagesResponse.ok) {
          const messagesData = await messagesResponse.json();
          // Filter out invalid messages
          const validMessages = (messagesData.messages || []).filter((msg: Message) => 
            msg.id && msg.created_at
          );
          setMessages(validMessages);
        }

        // Fetch session status
        await fetchSessionStatus();
      } catch (error) {
        console.error('Failed to fetch room data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoomData();
  }, [fetchSessionStatus, roomId, user?.id, user?.role, location.state]);

  useEffect(() => () => typingNotifier.stop(), [typingNotifier]);
  useEffect(() => {
    if (isStudentInputLocked) typingNotifier.stop();
  }, [isStudentInputLocked, typingNotifier]);

  // Clean up old typing indicators
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      setTypingUsers(prev => 
        prev.filter(user => now - user.timestamp < 3000)
      );
    }, 500);

    return () => clearInterval(cleanupInterval);
  }, []);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !roomId || (user?.role === 'student' && isAnswerInputLocked)) return;
    typingNotifier.stop();

    try {
      // If session is active, treat as an answer submission
      if (user?.role === 'student' && session?.active && session.session?.status === 'active') {
        setIsAnswerInputLocked(true);
        const response = await fetch(`/api/sessions/${roomId}/submit-answer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
          },
          body: JSON.stringify({ answer: newMessage.trim(), questionId: session.session.questionId })
        });

        if (response.ok) {
          setNewMessage('');
        } else {
          const error = await response.json();
          alert(error.error || 'Failed to submit answer');
        }
        return;
      }

      // Otherwise send as normal chat
      const response = await fetch(`/api/messages/room/${roomId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        },
        body: JSON.stringify({ content: newMessage.trim(), messageType: 'text' })
      });

      if (response.ok) {
        setNewMessage('');
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      await fetchSessionStatus();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    typingNotifier.update(e.target.value);
  };

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString();
    }
  };

  // Session management functions
  const startSession = async () => {
    if (!roomId || isStartingSession) return;

    setIsStartingSession(true);
    try {
      const response = await fetch(`/api/sessions/${roomId}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        await fetchSessionStatus();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to start session');
      }
    } catch (error) {
      console.error('Failed to start session:', error);
      alert('Failed to start session');
    } finally {
      setIsStartingSession(false);
    }
  };

  const pauseSession = async () => {
    if (!roomId) return;

    try {
      const response = await fetch(`/api/sessions/${roomId}/pause`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        await fetchSessionStatus();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to pause session');
      }
    } catch (error) {
      console.error('Failed to pause session:', error);
      alert('Failed to pause session');
    }
  };

  const resumeSession = async () => {
    if (!roomId) return;

    try {
      const response = await fetch(`/api/sessions/${roomId}/resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        await fetchSessionStatus();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to resume session');
      }
    } catch (error) {
      console.error('Failed to resume session:', error);
      alert('Failed to resume session');
    }
  };

  const completeSession = async () => {
    if (!roomId) return;

    try {
      const response = await fetch(`/api/sessions/${roomId}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('teaching_app_token')}`
        }
      });

      if (response.ok) {
        await fetchSessionStatus();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to complete session');
      }
    } catch (error) {
      console.error('Failed to complete session:', error);
      alert('Failed to complete session');
    }
  };

  const renderVoiceoverButton = (message: Message, className = '') => {
    const voiceText = getMessageVoiceText(message);
    if (!isSpeechSupported || !voiceText) {
      return null;
    }

    const isSpeaking = speakingMessageId === message.id;
    const radius = 15.5;
    const circumference = 2 * Math.PI * radius;
    const progressOffset = circumference * (1 - voiceoverProgress);
    const avatar = getVoiceoverAvatar(selectedAvatarId);

    return (
      <div className={`voiceover-control voiceover-control--${avatar.id} ${isSpeaking ? 'is-speaking' : ''}`}>
        <div className="voiceover-mascot" aria-hidden="true">
          <div className="voiceover-mascot-face">
            <img src={avatar.imageSrc} alt="" />
            <span className="voiceover-mascot-mouth" />
          </div>
        </div>
        <button
          type="button"
          onClick={() => isSpeaking ? stopVoiceover() : speakMessage(message)}
          className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${className}`}
          title={isSpeaking ? 'Stop voiceover' : 'Read aloud'}
          aria-label={isSpeaking ? 'Stop voiceover' : 'Read aloud'}
        >
          {isSpeaking && (
            <svg className="absolute inset-0 h-8 w-8 -rotate-90" viewBox="0 0 36 36" aria-hidden="true">
              <circle
                cx="18"
                cy="18"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.22"
                strokeWidth="2.5"
              />
              <circle
                cx="18"
                cy="18"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="2.5"
                strokeDasharray={circumference}
                strokeDashoffset={progressOffset}
              />
            </svg>
          )}
          {isSpeaking ? (
            <VolumeX className="relative h-4 w-4" />
          ) : (
            <Volume2 className="relative h-4 w-4" />
          )}
        </button>
      </div>
    );
  };

  const preventQuestionClipboard = (event: React.SyntheticEvent) => {
    event.preventDefault();
  };

  const renderMessage = (message: Message) => {
    // Skip invalid messages
    if (!message.id || !message.created_at) {
      return null;
    }
    
    const isOwnMessage = message.user_id === user?.id;
    
    if (message.message_type === 'system') {
      return (
        <div className="text-center w-full">
          <span className="inline-block px-3 py-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 rounded-full">
            {message.content || message.message_text}
          </span>
        </div>
      );
    }
    
    if (message.message_type === 'question') {
      return (
        <div className="flex justify-center w-full">
          <div className="max-w-2xl w-full">
            <div
              className="select-none bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4"
              onCopy={preventQuestionClipboard}
              onCut={preventQuestionClipboard}
              onContextMenu={preventQuestionClipboard}
              onDragStart={preventQuestionClipboard}
            >
              <div className="flex items-center justify-between gap-3 text-blue-700 dark:text-blue-300 mb-2">
                <div className="flex items-center">
                  <Target className="h-5 w-5 mr-2" />
                  <span className="font-medium">Current Question</span>
                </div>
                {renderVoiceoverButton(
                  message,
                  'text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-900/40'
                )}
              </div>
              <p className="text-blue-900 dark:text-blue-100 text-sm">{message.content || message.message_text}</p>
              {message.metadata?.stageId && (
                <div className="flex items-center text-xs text-blue-600 dark:text-blue-400 mt-2">
                  <Brain className="h-3 w-3 mr-1" />
                  Stage: {message.metadata.stageId}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    if (message.message_type === 'llm') {
      // Check if this is a welcome message
      if (message.metadata?.isWelcome) {
        return (
          <div className="flex justify-center w-full">
            <div className="max-w-2xl w-full">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                    <span className="font-medium text-sm text-blue-800 dark:text-blue-200">
                      AI Assistant
                    </span>
                  </div>
                  {renderVoiceoverButton(
                    message,
                    'text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-900/40'
                  )}
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {message.content}
                </p>
              </div>
            </div>
          </div>
        );
      }
      
      // Regular answer evaluation message
      const evaluationVoiceoverButton = renderVoiceoverButton(
        message,
        'text-gray-600 hover:text-gray-800 hover:bg-white/70 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800/50'
      );

      return (
        <div className="flex justify-center w-full">
          <div className="max-w-2xl w-full">
            <div className={`border rounded-lg p-4 ${
              (message.metadata?.score || 0) >= (room?.min_success_score || 70)
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  {(message.metadata?.score || 0) >= (room?.min_success_score || 70) ? (
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                  ) : (
                    <XCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                  )}
                  <span className="font-medium text-sm">
                    Answer Evaluation
                  </span>
                </div>
                <div className="text-sm font-semibold">
                  Score: {message.metadata?.score || 0}%
                </div>
              </div>
              {evaluationVoiceoverButton && (
                <div className="flex justify-end -mt-1 mb-1">
                  {evaluationVoiceoverButton}
                </div>
              )}
              <p className="text-sm mb-2">{message.metadata?.feedback || 'No feedback provided'}</p>
              {message.metadata?.hint && (
                <div className="flex items-center text-xs text-blue-600 dark:text-blue-400">
                  <Lightbulb className="h-3 w-3 mr-1" />
                  Hint: {message.metadata.hint}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    if (message.message_type === 'answer') {
      return (
        <div className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-xs lg:max-w-md ${isOwnMessage ? 'order-2' : 'order-1'}`}>
            {!isOwnMessage && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 ml-2">
              {message.user_name || message.user?.name || 'Unknown User'}
            </div>
            )}
            <div className={`px-4 py-2 rounded-2xl ${
              isOwnMessage
                ? 'bg-purple-600 text-white rounded-br-md'
                : 'bg-purple-100 dark:bg-purple-900/20 text-purple-900 dark:text-purple-100 border border-purple-200 dark:border-purple-800 rounded-bl-md'
            }`}>
              <div className="flex items-center mb-1">
                <Target className="h-3 w-3 mr-1" />
                <span className="text-xs font-medium">Answer</span>
              </div>
              <p className="text-sm">{message.content || message.message_text}</p>
            </div>
            <div className={`text-xs text-gray-500 dark:text-gray-400 mt-1 ${
              isOwnMessage ? 'text-right mr-2' : 'ml-2'
            }`}>
              {formatMessageTime(message.created_at)}
            </div>
          </div>
        </div>
      );
    }
    
    return <StudentChatMessage message={message} isOwnMessage={isOwnMessage} time={formatMessageTime(message.created_at)} />;
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return (
      <div className="min-h-[100svh] bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading chat room...</p>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-[100svh] bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Room not found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The room you're looking for doesn't exist or you don't have access to it.
          </p>
          <Link
            to="/rooms"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Back to Rooms
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100svh] h-[100svh] w-full bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-100/30 dark:from-gray-900 dark:via-slate-800 dark:to-slate-900 flex flex-col overflow-hidden">
      <PageHeader 
        title={room.name}
        icon={MessageSquare}
        iconColor="text-blue-600"
        backTo="/rooms"
        backText="Rooms"
        rightContent={
          <div className="flex items-center space-x-2">
            {isCurator() && room && room.created_by === user?.id && room.status !== 'archived' && (
              <>
                {!session?.active && (
                  <button 
                    onClick={startSession}
                    disabled={isStartingSession}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={isStartingSession ? "Starting session..." : "Start Session"}
                  >
                    <Play className="h-5 w-5" />
                  </button>
                )}
                {session?.active && session.session?.status === 'active' && (
                  <>
                    <button 
                      onClick={pauseSession}
                      className="p-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
                      title="Pause Session"
                    >
                      <Pause className="h-5 w-5" />
                    </button>
                    <button 
                      onClick={completeSession}
                      className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Complete Session"
                    >
                      <Square className="h-5 w-5" />
                    </button>
                  </>
                )}
                {session?.active && session.session?.status === 'paused' && (
                  <button 
                    onClick={resumeSession}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                    title="Resume Session"
                  >
                    <Play className="h-5 w-5" />
                  </button>
                )}
              </>
            )}
          </div>
        }
      />
      
      {/* Room Info Bar */}
      <div 
        className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-700/50 px-4 py-2 sm:mt-0"
        style={{
          marginTop: 'var(--header-height, 80px)'
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">
            {room.subject?.name} • {onlineUsers.size} online
          </span>
          {room.status === 'active' && room.current_question_text && (
            <div className="flex items-center text-blue-600 dark:text-blue-400">
              <Target className="h-4 w-4 mr-1" />
              <span className="text-xs">Session Active</span>
            </div>
          )}
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-800 p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages
            .filter(message => message.id && message.created_at) // Filter out invalid messages
            .map((message) => (
            <div key={message.id}>
              {renderMessage(message)}
            </div>
          ))}
          
          {/* Typing indicators */}
          {typingUsers.length > 0 && (
            <div className="flex justify-start">
              <div className="max-w-xs lg:max-w-md">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 ml-2">
                  {typingUsers.map(u => u.name).join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl rounded-bl-md px-4 py-2">
                  <div className="flex space-x-1">
                    <div key="dot-1" className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div key="dot-2" className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div key="dot-3" className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Session Status / LLM Thinking Display */}
      {(session?.active && session.session) || isLlmThinking ? (
        <div className="bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800 p-3">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center space-x-4 flex-wrap">
                {/* Left side: session / thinking state */}
                {isLlmThinking ? (
                  // When assistant is thinking, show only the thinking state
                  <div className="flex items-center text-blue-700 dark:text-blue-300 space-x-2">
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    <span className="text-sm font-medium">
                      {llmScope === 'peer_hint'
                        ? `${thinkingCharacterName} is typing…`
                        : llmScope === 'answer_eval'
                        ? 'Assistant is evaluating the answer…'
                        : 'Assistant is preparing the question…'}
                    </span>
                  </div>
                ) : (
                  <>
                    {session?.active && session.session && (
                      <>
                        {/* Session status based on session.status or room.status */}
                        {(() => {
                          const sessionStatus = session.session.status || room?.status || 'active';
                          const isStopped = sessionStatus === 'completed';
                          const isArchived = room?.status === 'archived';
                          const isPaused = sessionStatus === 'paused';
                          
                          // Determine status text and styling
                          let statusText = 'Session Active';
                          let statusColor = 'text-blue-700 dark:text-blue-300';
                          let statusIcon = Brain;
                          
                          if (isArchived) {
                            statusText = 'Session Archived';
                            statusColor = 'text-gray-600 dark:text-gray-400';
                            statusIcon = Archive;
                          } else if (isStopped) {
                            statusText = 'Session Completed';
                            statusColor = 'text-green-700 dark:text-green-300';
                            statusIcon = CheckCircle;
                          } else if (isPaused) {
                            statusText = 'Session Paused';
                            statusColor = 'text-yellow-700 dark:text-yellow-300';
                            statusIcon = Pause;
                          }
                          
                          const StatusIcon = statusIcon;
                          const showTimer = !isStopped && !isArchived;
                          
                          return (
                            <>
                              <div className={`flex items-center ${statusColor}`}>
                                <StatusIcon className="h-4 w-4 mr-2" />
                                <span className="text-sm font-medium">{statusText}</span>
                              </div>

                              {/* Question info (visible only when not stopped/archived) */}
                              {!isStopped && !isArchived && (
                                <div className="text-sm text-blue-600 dark:text-blue-400">
                                  Question {session.session.questionNumber ?? session.session.questionId}
                                </div>
                              )}

                              {/* Timer (visible only when not stopped/archived and not thinking) */}
                              {showTimer && timeRemaining !== null && timeLimitSeconds !== null && (
                                <div
                                  className={`flex items-center text-sm font-semibold ${
                                    timeRemaining <= 10
                                      ? 'text-red-600 dark:text-red-400'
                                      : timeRemaining <= 30
                                        ? 'text-orange-600 dark:text-orange-400'
                                        : 'text-blue-600 dark:text-blue-400'
                                  }`}
                                >
                                  <Clock className="h-4 w-4 mr-1" />
                                  {Math.floor(timeRemaining / 60)}:
                                  {(timeRemaining % 60).toString().padStart(2, '0')}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Removed secondary answer input; use chat box for answers during active session */}

      {/* Message Input */}

      <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center space-x-3">
            
            <div className="flex-1 relative">
              <input
                type="text"
                value={newMessage}
                onChange={handleInputChange}
                onBlur={typingNotifier.stop}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                disabled={isStudentInputLocked}
                className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${isStudentInputLocked ? 'opacity-60 cursor-not-allowed' : ''}`}
              />
            </div>
            
            <button
              onClick={handleSendMessage}
              aria-label="Send message"
              disabled={!newMessage.trim() || isStudentInputLocked}
              className="p-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Send className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
